import { useState } from "react";
import { Query, useQuery } from "@tanstack/react-query";
import { useKeycloak } from "@react-keycloak/web";
import { Organization } from "../../components/common/ObjectTypes";
import { fetchAllPages, PagedList } from "../fetchAllPages";
import { ACCOUNT_DATA_STALE_MS, queryClient } from "../queryClient";
import { queryKeys } from "../queryKeys";
import { loadOrganizations } from "./loadOrganizations";

// Saving a workspace refreshes it at once (see cacheRefresh); otherwise it is
// reused for ACCOUNT_DATA_STALE_MS before a page asks again.
const allOrganizationsQuery = (token: string) => ({
    queryKey: queryKeys.organizations.allPages(),
    queryFn: () => fetchAllPages<Organization>((offset, accumulated) => loadOrganizations(token, offset, "", accumulated)),
    // A list cut short by a failed page loads again on the next ask.
    staleTime: (query: Query<PagedList<Organization>>) => query.state.data?.partial ? 0 : ACCOUNT_DATA_STALE_MS,
});

// For callers outside the hook (permissions, analytics, route details). Shares
// the same cached list as useAllOrganizations, so nothing downloads it twice.
// On failure it falls back to the last good list (or [] if there is none), so a
// failed refetch never wipes the workspaces — callers don't expect a throw.
export const loadAllOrganizations = (token: string): Promise<Organization[]> => {
    const query = allOrganizationsQuery(token);
    return queryClient.fetchQuery(query)
        .then((list) => list.items)
        .catch(() => queryClient.getQueryData<PagedList<Organization>>(query.queryKey)?.items ?? []);
};

// Every workspace the user can see, shared by all pages that use it: the first
// page to mount fetches, the others reuse the result.
// `enabled: false` holds the list back, e.g. until the page's main table has loaded.
export const useAllOrganizations = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const { keycloak } = useKeycloak();
    const token = keycloak.token;
    const query = useQuery({
        ...allOrganizationsQuery(token as string),
        enabled: enabled && !!token && !keycloak.isTokenExpired(),
    });
    return {
        organizations: query.data?.items ?? [],
        loadingOrganizations: query.isLoading,
    };
};

// Typing this many characters into a workspace picker searches the server.
export const WORKSPACE_SEARCH_MIN_CHARS = 2;

// A workspace picker: every workspace, or the server's matches once the user
// has typed WORKSPACE_SEARCH_MIN_CHARS characters. One implementation, so every
// picker searches at the same point and shares the same cached answers.
export const useWorkspacePicker = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const { keycloak } = useKeycloak();
    const token = keycloak.token;
    const all = useAllOrganizations({ enabled });
    const [search, setSearch] = useState("");
    const searching = search.length >= WORKSPACE_SEARCH_MIN_CHARS;
    const query = useQuery({
        queryKey: queryKeys.organizations.search(search),
        queryFn: async () => {
            const [orgs, , error] = await loadOrganizations(token as string, 0, search, []);
            if (error) throw error;
            return orgs;
        },
        enabled: enabled && searching && !!token && typeof token === 'string' && !keycloak.isTokenExpired(),
    });
    return {
        organizations: searching && query.data ? query.data : all.organizations,
        loadingOrganizations: all.loadingOrganizations || query.isFetching,
        searchWorkspaces: (term: string) => setSearch(term.length >= WORKSPACE_SEARCH_MIN_CHARS ? term : ""),
        // Every workspace is already loaded; "more" only has to drop a search.
        clearWorkspaceSearch: () => setSearch(""),
    };
};
